/**
 * Encode an array buffer as Base64 string.
 * @param {Array|string} buffer            Array Buffer / String
 * @param {call} callback
 */
import {STK_DATATYPE_EXTENSION_MAPPING} from "../constants";

function encodeArrayBufferBase64(buffer) {
    let base64 = '';
    let encodings = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

    let bytes = new Uint8Array(buffer);
    let byteLength = bytes.byteLength;
    let byteRemainder = byteLength % 3;
    let mainLength = byteLength - byteRemainder;

    let a, b, c, d;
    let chunk;

    // Main loop deals with bytes in chunks of 3
    for (let i = 0; i < mainLength; i = i + 3) {
        // Combine the three bytes into a single integer
        chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];

        // Use bitmasks to extract 6-bit segments from the triplet
        a = (chunk & 16515072) >> 18; // 16515072 = (2^6 - 1) << 18
        b = (chunk & 258048) >> 12; // 258048   = (2^6 - 1) << 12
        c = (chunk & 4032) >> 6; // 4032     = (2^6 - 1) << 6
        d = chunk & 63;               // 63       = 2^6 - 1

        // Convert the raw binary segments to the appropriate ASCII encoding
        base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d];
    }

    // Deal with the remaining bytes and padding
    if (byteRemainder === 1) {
        chunk = bytes[mainLength];

        a = (chunk & 252) >> 2; // 252 = (2^6 - 1) << 2

        // Set the 4 least significant bits to zero
        b = (chunk & 3) << 4; // 3   = 2^2 - 1

        base64 += encodings[a] + encodings[b] + '==';
    } else if (byteRemainder === 2) {
        chunk = (bytes[mainLength] << 8) | bytes[mainLength + 1];

        a = (chunk & 64512) >> 10; // 64512 = (2^6 - 1) << 10
        b = (chunk & 1008) >> 4; // 1008  = (2^6 - 1) << 4

        // Set the 2 least significant bits to zero
        c = (chunk & 15) << 2; // 15    = 2^4 - 1

        base64 += encodings[a] + encodings[b] + encodings[c] + '=';
    }

    return base64;
}

/**
 * Encoding a given `array buffer` to `base64 string`
 * @param {ArrayBuffer} buffer Given array buffer
 * @return {string} The base64 string for given array buffer
 */
function newEncodeArrayBufferBase64(buffer) {
    let binaryString = '';
    let bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
        binaryString += String.fromCharCode(bytes[i]);
    }
    return btoa(binaryString);
}

/**
 * Decoding a given `base64 string` to `array buffer`
 * @param {string} base64 Given base64 string
 * @return {ArrayBufferLike} The array buffer for given base64 string
 */
function newDecodeBase64ToArrayBuffer(base64) {
    let binaryString = atob(base64);
    let length = binaryString.length;
    let bytes = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

/**
 * Get the extension of a file based on its name
 * @param {string} filename File name
 * @return {string} File extension prefix of a file
 */
function getFileExtension(filename) {
    // Get last `.`
    const lastIndex = filename.lastIndexOf(".");
    if (lastIndex === -1) {
        // There's no extension if it does not have `.`
        return "";
    }
    // Get the string after last `.` as extension of the image
    return filename.slice(lastIndex + 1) || "";
}

/**
 * Get the Base64 prefix of a file based on its filename
 * @param {string} filename File name
 * @return {string} Base64 prefix of a file
 */
function getBase64PrefixOfFile(filename) {
    let extension = getFileExtension(filename || "").toLowerCase();
    let datatype = STK_DATATYPE_EXTENSION_MAPPING[extension] || "";
    return `data:${datatype};base64,`;
}

export { encodeArrayBufferBase64, newEncodeArrayBufferBase64, newDecodeBase64ToArrayBuffer, getFileExtension, getBase64PrefixOfFile};
